import { MapPin, Phone, Mail, Clock, Shield, Heart, Award, Truck } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import toast from '@/hooks/useToast';

const values = [
  { icon: Shield, title: 'Authentic Medicines', desc: 'Every product is sourced directly from licensed manufacturers and distributors.' },
  { icon: Heart, title: 'Patient First', desc: 'Your health and well-being guide every decision we make at Milan Medical.' },
  { icon: Award, title: 'Expert Pharmacists', desc: 'Our licensed pharmacists review every prescription with diligence and care.' },
  { icon: Truck, title: 'Reliable Delivery', desc: 'Fast, temperature-controlled delivery across Nadiad and surrounding areas.' },
];

export const AboutPage = () => {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);

  const handleContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    await new Promise(r => setTimeout(r, 1000));
    setSending(false);
    toast.success('Message sent! We\'ll get back to you within 24 hours.');
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-12">
      {/* Hero */}
      <div className="text-center mb-16">
        <h1 className="font-heading font-bold text-4xl text-gray-800 mb-4">About Milan Medical</h1>
        <p className="text-gray-500 text-lg max-w-2xl mx-auto">
          Nadiad's trusted pharmacy and health store, committed to making quality healthcare accessible to every family since 2005.
        </p>
      </div>

      {/* Story */}
      <div className="grid md:grid-cols-2 gap-12 mb-16 items-center">
        <div>
          <h2 className="font-heading font-bold text-2xl text-gray-800 mb-4">Our Story</h2>
          <p className="text-gray-600 mb-4">
            Milan Medical was founded in 2005 by Dr. Milan Patel with a simple vision: to provide the people of Nadiad with genuine medicines, trusted health products, and professional pharmaceutical advice — all under one roof.
          </p>
          <p className="text-gray-600 mb-4">
            Over two decades, we've grown from a small neighbourhood pharmacy into one of Nadiad's most trusted health destinations, serving thousands of families across the district.
          </p>
          <p className="text-gray-600">
            In 2024, we launched our online store to bring that same trusted service to your doorstep, with licensed pharmacists reviewing every prescription and ensuring every order is genuine and safe.
          </p>
        </div>
        <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-2xl p-8">
          <div className="grid grid-cols-2 gap-6">
            {[
              { number: '20+', label: 'Years of Service' },
              { number: '50,000+', label: 'Happy Customers' },
              { number: '10,000+', label: 'Products' },
              { number: '5', label: 'Licensed Pharmacists' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <p className="font-heading font-bold text-3xl text-primary-600 mb-1">{s.number}</p>
                <p className="text-sm text-gray-600">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values */}
      <div className="mb-16">
        <h2 className="font-heading font-bold text-2xl text-gray-800 text-center mb-8">Our Values</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map(v => (
            <div key={v.title} className="bg-white rounded-card shadow-card p-6 text-center">
              <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
                <v.icon className="h-6 w-6 text-primary-600" />
              </div>
              <h3 className="font-heading font-semibold text-gray-800 mb-2">{v.title}</h3>
              <p className="text-gray-500 text-sm">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contact + Map */}
      <div className="grid md:grid-cols-2 gap-12 mb-16">
        {/* Contact Info */}
        <div>
          <h2 className="font-heading font-bold text-2xl text-gray-800 mb-6">Visit Us</h2>
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
                <MapPin className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Address</p>
                <p className="text-gray-500 text-sm">12, Milan Complex, College Road,<br />Nadiad - 387001, Gujarat, India</p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
                <Phone className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Phone</p>
                <a href="tel:+912682652000" className="text-gray-500 text-sm hover:text-primary-600">+91 2682 652000</a>
                <br />
                <a href="tel:+919898000000" className="text-gray-500 text-sm hover:text-primary-600">+91 98980 00000</a>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
                <Mail className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Email</p>
                <a href="mailto:hello@milanmedical.in" className="text-gray-500 text-sm hover:text-primary-600">hello@milanmedical.in</a>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="w-10 h-10 rounded-xl bg-primary-50 flex items-center justify-center shrink-0">
                <Clock className="h-5 w-5 text-primary-600" />
              </div>
              <div>
                <p className="font-medium text-gray-800">Store Hours</p>
                <p className="text-gray-500 text-sm">Mon–Sat: 8:00 AM – 9:00 PM</p>
                <p className="text-gray-500 text-sm">Sunday: 9:00 AM – 6:00 PM</p>
              </div>
            </div>
          </div>

          {/* Map placeholder */}
          <div className="mt-6 rounded-2xl overflow-hidden bg-gray-100 h-48 flex items-center justify-center border border-gray-200">
            <div className="text-center">
              <MapPin className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-400">Milan Medical, College Road, Nadiad</p>
              <a
                href="https://maps.google.com/?q=Nadiad+Gujarat+India"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-primary-600 hover:underline mt-1 inline-block"
              >
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div>
          <h2 className="font-heading font-bold text-2xl text-gray-800 mb-6">Send Us a Message</h2>
          <form onSubmit={handleContact} className="space-y-4">
            <Input
              label="Your Name"
              placeholder="Rahul Patel"
              value={form.name}
              onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
              required
            />
            <Input
              label="Email Address"
              type="email"
              placeholder="you@example.com"
              value={form.email}
              onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
              required
            />
            <Input
              label="Subject"
              placeholder="How can we help?"
              value={form.subject}
              onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Message</label>
              <textarea
                value={form.message}
                onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                placeholder="Tell us more..."
                rows={5}
                required
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none"
              />
            </div>
            <Button type="submit" className="w-full" loading={sending}>Send Message</Button>
          </form>
        </div>
      </div>
    </div>
  );
};
